import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import api from '../services/api';

export const ResetPassword = () => {
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token'), []);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => navigate('/login'), 3000);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInvalidToken(false);

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: newPassword });
      setSuccess(true);
    } catch (err) {
      if (err.response?.status === 400) {
        setInvalidToken(true);
      } else {
        setError(t('auth.invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <span style={{ color: 'white', fontWeight: 600, fontSize: '28px', letterSpacing: '-0.5px', lineHeight: 1 }}>AdamSched</span>
        </div>

        {success ? (
          <div className="space-y-4 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <p className="text-sm text-zinc-300">{t('auth.resetSuccess')}</p>
          </div>
        ) : invalidToken ? (
          <div className="space-y-4 text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto" />
            <p className="text-sm text-zinc-300">{t('auth.resetInvalidToken')}</p>
            <Button
              type="button"
              variant="outline"
              className="w-full h-10"
              onClick={() => navigate('/forgot-password')}
              data-testid="reset-password-request-new-link"
            >
              {t('auth.requestNewLink')}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <h1 className="text-lg font-semibold text-center text-white">{t('auth.resetPasswordTitle')}</h1>

            {error && (
              <div className="flex items-center gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="newPassword">{t('auth.newPassword')}</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input-tactical"
                required
                data-testid="reset-password-new"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-tactical"
                required
                data-testid="reset-password-confirm"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-primary h-10"
              disabled={loading}
              data-testid="reset-password-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth.resetPassword')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
